<script setup lang="ts">
// ===================================
// 1. IMPORTS
// ===================================
import { ref, computed } from 'vue';
import { TalentPoolForm, FormNavigation } from './talentPoolForm';
import { useTalentPoolsStore } from '@/stores/talentPoolsStore'
import { CircleX } from 'lucide-vue-next'
import { useProjectsStore } from '@/stores/projectsStore'
import { useTalentPoolModal } from '@/composables/useTalentPoolModal'

// ===================================
// 2. PROPS & EMITS
// ===================================

// ===================================
// 3. STATE
// ===================================
const { open, formRef, wizardMeta, show, close } = useTalentPoolModal()
const talentPoolsStore = useTalentPoolsStore()
const projectsStore = useProjectsStore()

// ===================================
// 7. FUNCTIONS
// ===================================

const handleSave = async () => {
  if (!formRef.value) return
  const { form, newProject, projectMode, selectedProject, editor } = formRef.value as any

  let projectId: number | null = null

  if (form.type === 'project') {
    if (projectMode === 'existing' && selectedProject) {
      projectId = selectedProject.id
    } else if (projectMode === 'new') {
      const project = await projectsStore.createProject(newProject)

      if (!project) {
        console.error('Project creation failed. Aborting talent pool creation.')
        return
      }

      projectId = project.id
    }
  }

  // --- Build payload
  const talentPoolPayload = {
    type: form.type,
    location: form.location.address.trim(),
    location_lat: form.location.lat,
    location_lng: form.location.lng,
    title: form.title.trim(),
    description: editor.getJSON(),
    workType: form.workType,
    rate: form.salary,
    rateType: form.salaryType,
    hiring_timeline: form.hiringTimeline,
    timeline: form.hiringTimeline === 'specific_date' ? form.timeline : null,
    requirements: form.requirements,
    qualifications: form.qualifications,
    projectId,
  }

  // --- Submit to store
  await talentPoolsStore.createTalentPool(talentPoolPayload)

  // --- Reset form & close modal
//   if (formRef.value.resetForm) {
    // formRef.value.resetForm()
//   }

  close()
}


defineExpose({ show, close })

</script>

<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="open"
        class="fixed inset-0 z-50 flex items-center justify-center"
        @keydown.esc="close"
        tabindex="0"
      >
        <div class="absolute inset-0 bg-black/50" @click="close"></div>

        <div class="relative bg-white rounded-2xl max-w-3xl shadow-lg w-11/12 flex flex-col max-h-[90vh]">
          <div class="px-6 py-4 border-b border-gray-200">
            <div class="flex justify-between items-center">
              <h2 class="text-xl font-semibold text-gray-900 leading-tight">
                Create Talent Pool
              </h2>
              <p class="text-sm text-gray-500">
                {{ wizardMeta.label }}
              </p>

              <div class="flex items-center gap-4">
                <span class="text-sm font-medium text-gray-500">
                  Step {{ wizardMeta.step }} of {{ wizardMeta.total }}
                </span>
                <CircleX class="h-6 w-6 text-gray-500 cursor-pointer" @click="close" />
              </div>
            </div>
          </div>

          <!-- Form -->
          <TalentPoolForm
            ref="formRef"
            mode="edit"
            :initial-data="null"
          />

          <!-- Form Navigation -->
          <FormNavigation
            v-if="formRef"
            :can-proceed="formRef.canProceedToNextStep"
            :is-first-step="formRef.currentStepIndex === 0"
            :is-last-step="formRef.currentStepIndex === formRef.steps.length - 1"
            @prev="formRef.decreaseStep()"
            @next="formRef.increaseStep()"
            @submit="handleSave"
            @cancel="close"
          />
        </div>
      </div>
    </transition>
  </teleport>
</template>
